import { Img } from 'openimg/react'
import { useRef } from 'react'
import { Link, Form, href } from 'react-router'
import { cn, getUserImgSrc } from '#app/utils/misc.tsx'
import { useUser } from '#app/utils/user.ts'
import { Button } from './ui/button'
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
} from './ui/dropdown-menu'
import { Icon } from './ui/icon'

export function UserDropdown({
	onOpenChange,
	className,
}: {
	onOpenChange?: (open: boolean) => void
	className?: string
}) {
	const user = useUser()
	const formRef = useRef<HTMLFormElement>(null)
	return (
		<DropdownMenu modal={false} onOpenChange={onOpenChange}>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					className={cn(
						'flex max-w-full min-w-0 items-center justify-start space-x-3 rounded-lg p-2 transition-colors',
						className,
					)}
				>
					<Img
						className="border-border size-8 shrink-0 rounded-full border object-cover"
						alt={user.name}
						src={getUserImgSrc(user.userImage?.objectKey)}
						width={256}
						height={256}
					/>
					<div className="flex min-w-0 flex-1 flex-col items-start">
						<span className="w-full truncate text-left text-sm font-medium">
							{user.name}
						</span>
						<span className="text-muted-foreground w-full truncate text-left text-xs">
							{user.email}
						</span>
					</div>
					<Icon
						name="dots-horizontal"
						className="text-muted-foreground h-4 w-4 shrink-0"
					/>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				sideOffset={8}
				align="end"
				className="border-border bg-popover text-popover-foreground w-64 rounded-xl border p-2 shadow-lg"
			>
				{/* User Info Header */}
				<div className="border-border mb-2 border-b px-3 py-2">
					<div className="flex items-center space-x-3">
						<Img
							className="border-border size-10 rounded-full border object-cover"
							alt={user.name}
							src={getUserImgSrc(user.userImage?.objectKey)}
							width={256}
							height={256}
						/>
						<div className="min-w-0 flex-1">
							<p className="text-foreground truncate text-sm font-medium">
								{user.name}
							</p>
							<p className="text-muted-foreground truncate text-xs">
								{user.email}
							</p>
						</div>
					</div>
				</div>

				{/* Menu Items */}
				<DropdownMenuItem asChild>
					<Link
						to={href('/dashboard')}
						className="text-foreground hover:bg-accent hover:text-accent-foreground flex items-center space-x-3 rounded-lg px-3 py-2 text-sm transition-colors"
					>
						<Icon name="laptop" className="h-4 w-4" />
						<span>Dashboard</span>
					</Link>
				</DropdownMenuItem>

				<DropdownMenuItem asChild>
					<Link
						to={href('/dashboard/settings/profile')}
						className="text-foreground hover:bg-accent hover:text-accent-foreground flex items-center space-x-3 rounded-lg px-3 py-2 text-sm transition-colors"
					>
						<Icon name="avatar" className="h-4 w-4" />
						<span>Profile Settings</span>
					</Link>
				</DropdownMenuItem>

				<DropdownMenuItem asChild>
					<Link
						to={href('/dashboard/websites')}
						className="text-foreground hover:bg-accent hover:text-accent-foreground flex items-center space-x-3 rounded-lg px-3 py-2 text-sm transition-colors"
					>
						<Icon name="link-2" className="h-4 w-4" />
						<span>My Websites</span>
					</Link>
				</DropdownMenuItem>

				<DropdownMenuItem asChild>
					<Link
						to={href('/dashboard/scans')}
						className="text-foreground hover:bg-accent hover:text-accent-foreground flex items-center space-x-3 rounded-lg px-3 py-2 text-sm transition-colors"
					>
						<Icon name="magnifying-glass" className="h-4 w-4" />
						<span>Recent Scans</span>
					</Link>
				</DropdownMenuItem>

				{/* Divider */}
				<div className="border-border my-2 border-t"></div>

				{/* Support Links */}
				<DropdownMenuItem asChild>
					<Link
						to={href('/support')}
						className="text-foreground hover:bg-accent hover:text-accent-foreground flex items-center space-x-3 rounded-lg px-3 py-2 text-sm transition-colors"
					>
						<Icon name="question-mark-circled" className="h-4 w-4" />
						<span>Help & Support</span>
					</Link>
				</DropdownMenuItem>

				{/* Logout */}
				<div className="border-border mt-2 border-t pt-2">
					<Form action="/logout" method="POST" ref={formRef}>
						<DropdownMenuItem asChild>
							<button
								type="submit"
								className="text-destructive hover:bg-destructive/10 flex w-full items-center space-x-3 rounded-lg px-3 py-2 text-sm transition-colors"
							>
								<Icon name="exit" className="h-4 w-4" />
								<span>Sign Out</span>
							</button>
						</DropdownMenuItem>
					</Form>
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
